import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Breadcrumbs, Button, FormWrapper, ModalWrapper, SubmitButton } from "../components";
import CreditsTable from "../components/table/CreditsTable";
import { usePayments } from "../hooks";
import { parseAmount } from "../services/utils";

function CreditsPage() {
  const paymentsCreditColumns = [
    { id: "project_name", name: "Project Name" },
    { id: "company_name", name: "Company Name" },
    { id: "customer_name", name: "Customer Name" },
    { id: "plot_number", name: "Plot No." },
    { id: "payment_type", name: "Amount Type" },
    { id: "amount", name: "Amount" },
    { id: "payment_method", name: "Method of payment" },
    { id: "reference_number", name: "Reference Number" },
    // { id: "due_date", name: "Due Date" },
    { id: "date_received", name: "Date Received" },
    { id: "late_days", name: "Late Days" },
    { id: "payment_proof", name: "Attach Screenshot" },
    { id: "action", name: "Status" },
  ];

  const commissionCreditColumns = [
    { id: "project_name", name: "Project Name" },
    { id: "company_name", name: "Company Name" },
    { id: "owner_name", name: "Owner" },
    { id: "amount", name: "Amount" },
    { id: "payment_method", name: "Method of payment" },
    { id: "reference_number", name: "Reference Number" },
    { id: "payment_proof", name: "Attach Screenshot" },
    { id: "action", name: "Status" },
  ];

  const [paymentsCreditData, setPaymentsCreditData] = useState([]);
  const [commissionCreditData, setCommissionCreditData] = useState([]);
  const [showAcceptPaymentModal, setShowAcceptPaymentModal] = useState(false);
  const [updateButtonClicked, setUpdateButtonClicked] = useState(false);

  const { fetchCommissionPayments, fetchReceivedPayments, updatePaymentStatus } = usePayments();

  const fetchPaymentsCreditData = async () => {
    const { payments, error } = await fetchReceivedPayments();
    if (!payments) return toast.error(error);
    setPaymentsCreditData(payments);
  };

  const fetchCommissionCreditData = async () => {
    const { payments, error } = await fetchCommissionPayments();
    if (!payments) return toast.error(error);
    setCommissionCreditData(payments);
  };

  useEffect(() => {
    fetchPaymentsCreditData();
    fetchCommissionCreditData();
  }, []);

  const [amountCredited, setAmountCredited] = useState("");
  const [amountConsidered, setAmountConsidered] = useState("");
  const [waiveOff, setWaiveOff] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [paymentId, setPaymentId] = useState("");
  const handlePaymentAction = async (id, action, amount, payment_type) => {
    if (action === "approve" && (payment_type === "emi" || payment_type === "downpayment")) {
      setPaymentId(id);
      setAmountCredited(amount.replaceAll(",", ""));
      setPaymentType(payment_type);
      setShowAcceptPaymentModal(true);
    } else {
      await updatePayment(id, { status: action, amount: amount.replaceAll(",", "") });
    }
  };

  const handleAcceptPayment = async (e) => {
    e.preventDefault();
    setUpdateButtonClicked(true);
    if (
      isNaN(waiveOff) ||
      waiveOff < 0 ||
      waiveOff > +amountCredited ||
      isNaN(amountConsidered) ||
      amountConsidered < 0 ||
      amountConsidered > +amountCredited ||
      amountConsidered + waiveOff !== +amountCredited
    ) {
      return toast.error("Please enter a valid amount");
    }

    let dataToSubmit = {
      waive_off_amount: waiveOff,
      amount: amountConsidered,
      status: "approve",
    };
    await updatePayment(paymentId, dataToSubmit);
  };

  const updatePayment = async (id, data) => {
    const { success } = await updatePaymentStatus(id, data);
    if (!success) {
      return toast.error("Failed to update payment");
    }
    handleCloseAcceptPaymentModal();
    toast.success("Payment updated successfully.");
    await fetchPaymentsCreditData();
  };

  const handleCommissionAction = async (id, action, amount, payment_type) => {
    const amountValue = +amount.replaceAll(",", "").replace("₹", "").replaceAll(" ", "").replace(".00", "");
    const { success } = await updatePaymentStatus(id, { status: action, amount: amountValue });
    if (!success) {
      return toast.error("Failed to update payment");
    }
    await fetchCommissionCreditData();
  };

  const handleCloseAcceptPaymentModal = () => {
    setUpdateButtonClicked(false);
    setShowAcceptPaymentModal(false);
    setAmountCredited("");
    setAmountConsidered(0);
    setWaiveOff("");
    setPaymentType("");
    setPaymentId("");
  };

  const paymentTypeText = {
    token: "Token",
    registrationcharges: "Registration Charges",
    developmentcharges: "Development Charges",
    downpayment: "Down Payment",
    emi: "EMI",
    booking_advance: "Booking Advance",
  };
  return (
    <>
      <Breadcrumbs />
      <CreditsTable
        columns={paymentsCreditColumns}
        data={paymentsCreditData}
        title="Payment Received (From Customers)"
        handlePaymentAction={handlePaymentAction}
      />
      <CreditsTable
        columns={commissionCreditColumns}
        data={commissionCreditData}
        title="Commission Received (From Project Owners)"
        handlePaymentAction={handleCommissionAction}
      />
      {showAcceptPaymentModal &&
        createPortal(
          <ModalWrapper
            name="accept_payment_modal"
            isVisible={showAcceptPaymentModal}
            onClose={handleCloseAcceptPaymentModal}
          >
            <FormWrapper id="accept_payment_form" onSubmit={handleAcceptPayment}>
              <div className="column">
                <div className="row">
                  <span className="label">Amount credited</span>
                  <span className="value">{parseAmount(amountCredited)}</span>
                </div>
                <div className="row">
                  <span className="label">{paymentTypeText[paymentType] || paymentType}</span>
                  <div className="input_with_prefix_wrapper">
                    <span className="prefix">₹</span>
                    <input
                      type="text"
                      name="amount-considered"
                      value={isNaN(amountConsidered) ? "" : amountConsidered}
                      onChange={(e) => {
                        if (isNaN(+e.target.value)) return;
                        setAmountConsidered(+e.target.value);
                        setWaiveOff(amountCredited - +e.target.value);
                      }}
                      className="input"
                      placeholder="Enter amount"
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <span className="label">Waived off amount</span>
                  <div className="input_with_prefix_wrapper">
                    <span className="prefix">₹</span>
                    <input
                      type="text"
                      name="waive-off"
                      value={isNaN(waiveOff) ? "" : waiveOff}
                      onChange={(e) => {
                        if (isNaN(+e.target.value)) return;
                        setWaiveOff(+e.target.value);
                        setAmountConsidered(amountCredited - +e.target.value);
                      }}
                      className="input"
                      placeholder="Enter amount"
                      required
                    />
                  </div>
                </div>

                <div className="row center">
                  <Button
                    className="ghost_gray"
                    onClick={() => {
                      handleCloseAcceptPaymentModal();
                    }}
                  >
                    Cancel
                  </Button>
                  <SubmitButton className="cta" form="accept_payment_form" disabled={updateButtonClicked}>
                    Save
                  </SubmitButton>
                </div>
              </div>
            </FormWrapper>
          </ModalWrapper>,
          document.body
        )}
    </>
  );
}

export default CreditsPage;
